import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Link, { LocalizedNavigate as Navigate } from "../components/LocalizedLink";
import { useI18n } from "../i18n/I18nProvider";
import { api, resolveImage } from "../lib/api";
import HeroMedia, { HERO_BG } from "../components/HeroMedia";
import DevelopmentCard from "../components/DevelopmentCard";
import SchemaMarkup, { buildBreadcrumb } from "../components/SchemaMarkup";
import useDocumentMeta from "../hooks/useDocumentMeta";

const KEYS = {
  "frontline-golf": "category-frontline-golf",
  "sea-view": "category-sea-view",
};

export default function CategoryPage() {
  const { category } = useParams();
  const key = KEYS[category];
  const [page, setPage] = useState(null);
  const [items, setItems] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const { t } = useI18n();
  const error = !key || fetchError;

  useEffect(() => {
    if (!key) return undefined;
    let cancel = false;
    (async () => {
      try {
        const { data: p } = await api.get(`/pages/${key}`);
        if (cancel) return;
        setPage(p);
        const { data: devs } = await api.get(`/developments`, {
          params: { category: p.category_filter || category },
        });
        if (cancel) return;
        setItems(devs);
      } catch (_err) {
        if (!cancel) setFetchError(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [key, category]);

  useDocumentMeta({
    title: page?.seo?.title || page?.title || "",
    description: page?.seo?.description || "",
  });

  if (error || !key) return <Navigate to="/developments" replace />;
  if (!page) return <div className="min-h-screen bg-[#F9F6F0]" />;

  return (
    <div className="bg-[#F9F6F0] text-[#1A1A1A]">
      <SchemaMarkup
        schema={buildBreadcrumb([
          { name: t("home"), url: "/" },
          { name: t("developments"), url: "/developments" },
          { name: page.title, url: `/categories/${category}` },
        ])}
      />

      <section className={`relative h-[70svh] min-h-[460px] overflow-hidden ${HERO_BG}`}>
        <HeroMedia src={page.hero_image ? resolveImage(page.hero_image) : ""} alt={page.title} overlay="bg-gradient-to-b from-black/45 via-black/20 to-black/55" />
        <div className="relative z-10 h-full flex flex-col justify-end max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-16 lg:pb-20">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#E5C99A] mb-4">
            {page.eyebrow || t("category_default_eyebrow")}
          </p>
          <h1
            data-testid="category-title"
            className="text-5xl sm:text-6xl lg:text-7xl text-[#F9F6F0] font-light leading-[0.95] max-w-4xl"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            dangerouslySetInnerHTML={{ __html: page.heading || "" }}
          />
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <div
            className="prose prose-stone max-w-none text-lg lg:text-xl font-light leading-relaxed text-[#1A1A1A]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            dangerouslySetInnerHTML={{ __html: page.intro || "" }}
          />
        </div>
      </section>

      <section className="pb-24 lg:pb-32 bg-[#FFFFFF] border-y border-[#E5DFD5] pt-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-3">
            {t("category_curated_count", { count: items?.length || 0 })}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16 mt-10">
            {(items || []).map((d, i) => (
              <DevelopmentCard key={d.slug} d={d} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1A1A1A] text-[#F9F6F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h2
            className="text-3xl lg:text-4xl font-light max-w-2xl leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t("consolidated_price_list_plans")}
          </h2>
          <Link
            to="/contact?request=price-list"
            data-testid="category-cta"
            className="bg-[#C5A880] text-[#1A1A1A] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#F9F6F0] transition-colors"
          >
            {t("request_price_list")}
          </Link>
        </div>
      </section>
    </div>
  );
}
